import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { User, Calendar, Crown } from 'lucide-react';
import { format } from 'date-fns';

export function RecentUsersCard({ users, title = "Recent Users", compact = false }) {
  const getBadgeVariant = (tier) => {
    switch (tier) {
      case 'pro':
        return 'default';
      case 'free':
      default:
        return 'secondary';
    }
  };

  const getTierIcon = (tier) => {
    if (tier === 'pro') {
      return <Crown className="h-3 w-3 text-yellow-500" />;
    }
    return <User className="h-3 w-3 text-gray-500" />;
  };

  return (
    <Card>
      <CardHeader className={compact ? 'pb-2' : ''}>
        <CardTitle className={`${compact ? 'text-sm' : 'text-lg'} font-semibold text-gray-800 flex items-center`}>
          <User className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} mr-2`} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={compact ? 'space-y-2' : 'space-y-4'}>
          {users.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              <User className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-xs">No users found</p>
            </div>
          ) : (
            users.slice(0, compact ? 5 : users.length).map((user, index) => (
              <div key={user.user_id || index} className={`flex items-center justify-between ${compact ? 'p-2' : 'p-3'} bg-gray-50 rounded-lg`}>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    {getTierIcon(user.subscription_tier)}
                    <div>
                      <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-900`}>
                        {user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name}`
                          : `User ${user.user_id?.slice(-8) || 'Unknown'}`
                        }
                      </p>
                      <div className={`flex items-center ${compact ? 'text-xs' : 'text-xs'} text-gray-500`}>
                        <Calendar className="h-3 w-3 mr-1" />
                        {user.created_at ? format(new Date(user.created_at), 'MMM dd') : 'Unknown'}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant={getBadgeVariant(user.subscription_tier)} className={compact ? 'text-xs px-1 py-0' : ''}>
                    {user.subscription_tier || 'free'}
                  </Badge>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function ActivityOverviewCard({ data, title = "Platform Activity Overview" }) {
  const stats = [
    {
      label: 'Total Users',
      value: data.totalUsers,
      color: 'bg-blue-100 text-blue-800'
    },
    {
      label: 'Project Groups',
      value: data.totalProjectGroups,
      color: 'bg-green-100 text-green-800'
    },
    {
      label: 'Projects',
      value: data.totalProjects,
      color: 'bg-purple-100 text-purple-800'
    },
    {
      label: 'Tasks',
      value: data.totalTasks,
      color: 'bg-yellow-100 text-yellow-800'
    }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-gray-800">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${stat.color} mb-2`}>
                <span className="text-2xl font-bold">
                  {stat.value > 999 ? `${Math.floor(stat.value / 1000)}k` : stat.value}
                </span>
              </div>
              <p className="text-sm text-gray-600">{stat.label}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
